import { useEffect, useState, useRef } from "react";
import { Camera, IdCard, MapPin, Save, Locate, FileText, CheckCircle2, IndianRupee } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import api, { errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useLocations from "@/hooks/useLocations";
import useBranches from "@/hooks/useBranches";

const MAX_MB = 5;

const fileToB64 = (file, onProgress) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onprogress = (ev) => { if (ev.lengthComputable && onProgress) onProgress(Math.round((ev.loaded / ev.total) * 100)); };
  r.onload = () => { if (onProgress) onProgress(100); resolve(r.result); };
  r.onerror = reject;
  r.readAsDataURL(file);
});

export default function BankStaffEditProfileDialog({ open, onClose }) {
  const { user } = useAuth();
  const { districts } = useLocations();
  const distObj = districts.find(d => (d.name || "").toUpperCase() === (user?.district || "").toUpperCase());
  const { branches } = useBranches(distObj?.id);

  const [loanTypes, setLoanTypes] = useState([]); // [{ id, name }]
  const [f, setF] = useState({
    photo_b64: user?.photo_b64 || "",
    id_card_b64: user?.id_card_b64 || "",
    branch_id: user?.branch_id || "",
    loan_types: user?.loan_types || [],
    min_amount: user?.min_amount || "",
    max_amount: user?.max_amount || "",
    connector_commissions: user?.connector_commissions || {}, // { LOAN_NAME: {pct, min, max} }
    branch_lat: user?.branch_lat ?? null,
    branch_lng: user?.branch_lng ?? null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [pending, setPending] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(0);
  const [idUploading, setIdUploading] = useState(0);
  const photoRef = useRef();
  const idRef = useRef();

  useEffect(() => {
    if (!open) return;
    api.get("/bank-staff/profile-request").then(r => setPending(r.data?.id ? r.data : null)).catch(() => {});
    api.get("/loans").then(r => setLoanTypes((r.data || []).filter(l => (l.status || "active") !== "inactive"))).catch(() => setLoanTypes([]));
  }, [open]);

  const handleFile = (setter, setProgress) => async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`File too large — max ${MAX_MB}MB`);
      e.target.value = "";
      return;
    }
    setProgress(1);
    try {
      const b64 = await fileToB64(file, setProgress);
      setter(b64);
      toast.success(`${file.name} uploaded (${(file.size / 1024).toFixed(0)} KB)`);
    } catch {
      toast.error("Could not read the file. Try a smaller image.");
    } finally {
      setTimeout(() => setProgress(0), 400);
      e.target.value = ""; // allow re-selecting same file
    }
  };

  const pinLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation not supported. HTTPS + supported browser required.");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setF(p => ({ ...p, branch_lat: +pos.coords.latitude.toFixed(6), branch_lng: +pos.coords.longitude.toFixed(6) }));
        toast.success("Branch GPS pinned");
      },
      (err) => {
        if (err.code === 1) alert("Please allow location");
        else toast.error("Could not fetch location — try again outdoors.");
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  const toggleLoanType = (name) => {
    setF(p => {
      const has = p.loan_types.includes(name);
      const next_lt = has ? p.loan_types.filter(x => x !== name) : [...p.loan_types, name];
      const next_cc = { ...p.connector_commissions };
      if (has) delete next_cc[name];
      else if (!next_cc[name]) next_cc[name] = { pct: "", min: "", max: "" };
      return { ...p, loan_types: next_lt, connector_commissions: next_cc };
    });
  };

  const setCommField = (lt, key, raw) => {
    setF(p => {
      const cur = p.connector_commissions[lt] || { pct: "", min: "", max: "" };
      const val = raw === "" ? "" : (key === "pct" ? parseFloat(raw) : parseInt(raw.replace(/\D/g, ""), 10));
      return { ...p, connector_commissions: { ...p.connector_commissions, [lt]: { ...cur, [key]: Number.isNaN(val) ? "" : val } } };
    });
  };

  const submit = async () => {
    if (!f.photo_b64) return toast.error("Please upload your photo");
    if (!f.id_card_b64) return toast.error("Please upload your ID card");
    if (!f.branch_id) return toast.error("Pick a Branch");
    if (!f.loan_types.length) return toast.error("Select at least one Loan Type");
    const mn = parseFloat(f.min_amount || 0), mx = parseFloat(f.max_amount || 0);
    if (mn <= 0 || mx <= 0 || mn > mx) return toast.error("Enter valid Min ≤ Max amount");
    // Validate commissions for each selected loan type
    const norm = {};
    for (const lt of f.loan_types) {
      const e = f.connector_commissions[lt] || {};
      const pct = e.pct === "" || e.pct === undefined ? null : Number(e.pct);
      if (pct === null || Number.isNaN(pct) || pct < 0) return toast.error(`${lt}: enter commission %`);
      const cmin = e.min === "" || e.min === undefined ? null : Number(e.min);
      const cmax = e.max === "" || e.max === undefined ? null : Number(e.max);
      if (cmin != null && cmax != null && cmin > cmax) return toast.error(`${lt}: Min cannot exceed Max`);
      norm[lt] = { pct, min: cmin, max: cmax };
    }
    if (f.branch_lat === null || f.branch_lng === null) return toast.error("Pin your Branch GPS location");

    setSubmitting(true);
    try {
      await api.post("/bank-staff/profile-request", {
        photo_b64: f.photo_b64 || null,
        id_card_b64: f.id_card_b64 || null,
        branch_id: f.branch_id,
        loan_types: f.loan_types,
        min_amount: mn,
        max_amount: mx,
        connector_commissions: norm,
        branch_lat: f.branch_lat,
        branch_lng: f.branch_lng,
      });
      toast.success("Sent for Admin approval · Status: APPROVAL WAITING");
      onClose();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o)=>!o && onClose()}>
      <DialogContent className="bg-[#0F172A] border-[#C5A028]/30 text-white max-w-md max-h-[92vh] overflow-y-auto" data-testid="bank-staff-edit-profile">
        <DialogHeader>
          <DialogTitle className="text-[#FFD700] flex items-center gap-2"><Save className="w-4 h-4"/> Edit Profile</DialogTitle>
        </DialogHeader>

        {pending && (
          <div className="rounded-lg bg-[#FFD700]/10 border border-[#FFD700]/30 p-2.5 text-xs text-[#FFD700]" data-testid="pending-notice">
            You already have a pending change request. Submitting again will replace it.
          </div>
        )}

        {!pending && user?.last_reject_reason && (
          <div className="rounded-lg bg-red-500/10 border border-red-500/40 p-2.5 text-xs text-red-300" data-testid="reject-notice">
            <div className="font-bold text-red-200 mb-1">Last update was rejected by Admin</div>
            <div className="text-red-300/90">Reason: {user.last_reject_reason}</div>
            <div className="text-red-300/70 mt-1 text-[10px]">Fix the issue and resubmit. Cases will only reach you after Admin approval.</div>
          </div>
        )}

        {/* Photo — LABEL wraps input for reliable mobile tap */}
        <div className="flex flex-col items-center" data-testid="photo-block">
          <label htmlFor="bstaff-photo-file" className="relative w-24 h-24 rounded-full bg-[#141A2E] border-2 border-[#FFD700]/40 flex items-center justify-center overflow-hidden hover:border-[#FFD700] cursor-pointer" data-testid="photo-btn">
            {f.photo_b64 ? <img src={f.photo_b64} className="w-full h-full object-cover" alt="avatar"/> : <Camera className="w-7 h-7 text-slate-500"/>}
            <div className="absolute bottom-0 inset-x-0 bg-[#FFD700] text-slate-900 text-[9px] font-bold py-0.5 flex items-center justify-center gap-1">
              <Camera className="w-2.5 h-2.5"/> {f.photo_b64 ? "Change" : "Upload"}
            </div>
            {photoUploading > 0 && photoUploading < 100 && (
              <div className="absolute inset-0 bg-slate-950/70 flex items-center justify-center text-[10px] font-bold text-[#FFD700]">{photoUploading}%</div>
            )}
          </label>
          <input
            ref={photoRef}
            id="bstaff-photo-file"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleFile(v=>setF(p=>({...p, photo_b64:v})), setPhotoUploading)}
            data-testid="photo-input"
          />
          <div className="text-[10px] text-slate-500 mt-1">JPG/PNG · max {MAX_MB}MB</div>
        </div>

        {/* ID Card */}
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">ID Card (Aadhaar / Bank ID) *</Label>
          <div className="mt-1">
            {f.id_card_b64 ? (
              <label htmlFor="bstaff-id-file" className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-2 flex items-center gap-2 cursor-pointer" data-testid="id-uploaded">
                {f.id_card_b64.startsWith("data:application/pdf") ? <FileText className="w-8 h-8 text-red-400"/> : <img src={f.id_card_b64} className="w-14 h-14 rounded object-cover" alt="id"/>}
                <div className="flex-1 text-xs"><div className="text-emerald-400 font-bold flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/> Uploaded</div><div className="text-slate-500 text-[10px]">Tap to replace</div></div>
                <button type="button" onClick={(e) => { e.preventDefault(); setF(p=>({...p, id_card_b64:""})); }} className="text-red-400 text-xs">Remove</button>
              </label>
            ) : (
              <label htmlFor="bstaff-id-file" data-testid="id-btn" className="block w-full rounded-lg border-2 border-dashed border-slate-700 hover:border-[#FFD700]/60 hover:bg-[#FFD700]/5 p-3 text-center text-slate-400 cursor-pointer">
                <IdCard className="w-5 h-5 mx-auto mb-1 text-slate-500"/>
                <div className="text-xs font-semibold">Tap to upload ID</div>
                <div className="text-[10px] text-slate-600 mt-0.5">PDF / JPG / PNG · max {MAX_MB}MB</div>
                {idUploading > 0 && idUploading < 100 && <div className="mt-2 text-[10px] text-[#FFD700] font-bold">{idUploading}%</div>}
              </label>
            )}
            <input
              ref={idRef}
              id="bstaff-id-file"
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              className="hidden"
              onChange={handleFile(v=>setF(p=>({...p, id_card_b64:v})), setIdUploading)}
              data-testid="id-input"
            />
          </div>
        </div>

        {/* Branch */}
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Branch *</Label>
          <Select value={f.branch_id} onValueChange={v=>setF(p=>({...p, branch_id:v}))}>
            <SelectTrigger data-testid="branch-select" className="bg-slate-950 border-slate-700 h-11 mt-1"><SelectValue placeholder={distObj ? "Select branch" : "Ask admin to set district"}/></SelectTrigger>
            <SelectContent className="bg-[#0F172A] text-white border-slate-700 max-h-64">
              {branches.length === 0 ? <div className="p-3 text-sm text-slate-400">No branches for {user?.district || "your district"} yet — ask admin.</div> : branches.map(b => <SelectItem key={b.id} value={b.id}>{b.branch_name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="text-[9px] text-slate-500 mt-1">District: <b className="text-slate-300">{user?.district || "—"}</b></div>
        </div>

        {/* Loan Types multi-select — dynamic */}
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Loan Types * <span className="text-slate-500 normal-case">({loanTypes.length} available)</span></Label>
          {loanTypes.length === 0 ? (
            <div className="mt-1.5 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
              No loan products configured yet. Ask Admin to add loans in <b>Admin › Loans</b>.
            </div>
          ) : (
            <div className="mt-1.5 grid grid-cols-1 gap-1.5" data-testid="loan-types-list">
              {loanTypes.map(lt => {
                const slug = lt.name.replace(/\s+/g, "-").replace(/[^A-Za-z0-9_-]/g, "").toLowerCase();
                const checked = f.loan_types.includes(lt.name);
                return (
                  <label key={lt.id || lt.name} className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${checked ? "bg-[#FFD700]/10 border-[#FFD700]/40" : "bg-slate-950 border-slate-700 hover:border-[#FFD700]/40"}`} data-testid={`lt-${slug}`}>
                    <input type="checkbox" checked={checked} onChange={() => toggleLoanType(lt.name)} className="accent-[#FFD700]"/>
                    <span className="text-sm font-semibold">{lt.name}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Amount Range */}
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Loan Amount Range (₹) *</Label>
          <div className="grid grid-cols-2 gap-2 mt-1.5">
            <Input data-testid="min-amt" inputMode="numeric" placeholder="Min (e.g. 500000)" value={f.min_amount} onChange={e=>setF(p=>({...p, min_amount: e.target.value.replace(/\D/g,"")}))} className="bg-slate-950 border-slate-700 h-11 font-mono-alt"/>
            <Input data-testid="max-amt" inputMode="numeric" placeholder="Max (e.g. 5000000)" value={f.max_amount} onChange={e=>setF(p=>({...p, max_amount: e.target.value.replace(/\D/g,"")}))} className="bg-slate-950 border-slate-700 h-11 font-mono-alt"/>
          </div>
        </div>

        {/* MY COMMISSION TO SAKTHI — per selected loan type, 3 fields */}
        <div>
          <Label className="text-[#FFD700] text-[10px] uppercase tracking-widest font-black flex items-center gap-1.5">
            <IndianRupee className="w-3 h-3"/> My Commission to Sakthi Consultancy
          </Label>
          <p className="text-[10px] text-slate-500 mt-1 mb-2">For each selected loan type, enter the % you pay Sakthi and the loan amount band this rate applies to.</p>
          {f.loan_types.length === 0 ? (
            <div className="text-[11px] text-slate-500 bg-slate-950 border border-slate-800 rounded-lg p-3">Select loan types above to enter commissions.</div>
          ) : (
            <div className="space-y-2" data-testid="commissions-list">
              {f.loan_types.map(lt => {
                const slug = lt.replace(/\s+/g, "-").replace(/[^A-Za-z0-9_-]/g, "").toLowerCase();
                const e = f.connector_commissions[lt] || { pct: "", min: "", max: "" };
                return (
                  <div key={lt} className="bg-slate-950/60 border border-slate-800 rounded-lg p-2.5" data-testid={`comm-row-${slug}`}>
                    <div className="text-[11px] font-bold text-white mb-1.5">{lt}</div>
                    <div className="grid grid-cols-3 gap-1.5">
                      <div className="relative">
                        <Input data-testid={`comm-pct-${slug}`} type="number" step="0.1" placeholder="Comm %" value={e.pct} onChange={ev => setCommField(lt, "pct", ev.target.value)} className="bg-slate-900 border-slate-700 h-9 pr-6 text-xs"/>
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-[10px]">%</span>
                      </div>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-[10px]">₹</span>
                        <Input data-testid={`comm-min-${slug}`} inputMode="numeric" placeholder="Min amt" value={e.min} onChange={ev => setCommField(lt, "min", ev.target.value)} className="bg-slate-900 border-slate-700 h-9 pl-6 text-xs"/>
                      </div>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-[10px]">₹</span>
                        <Input data-testid={`comm-max-${slug}`} inputMode="numeric" placeholder="Max amt" value={e.max} onChange={ev => setCommField(lt, "max", ev.target.value)} className="bg-slate-900 border-slate-700 h-9 pl-6 text-xs"/>
                      </div>
                    </div>
                    <div className="text-[9px] text-slate-500 mt-1 flex justify-between px-1"><span>Commission %</span><span>Min</span><span>Max</span></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Branch GPS */}
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5"><MapPin className="w-3 h-3 text-[#FFD700]"/> Branch GPS Location *</Label>
          <Button type="button" data-testid="pin-branch-gps" onClick={pinLocation} className={`mt-1.5 w-full h-11 rounded-full font-bold text-sm ${f.branch_lat!==null ? "bg-emerald-500 text-slate-900 hover:bg-emerald-400" : "bg-[#FFD700] text-slate-900 hover:bg-[#f0c800]"}`}>
            <Locate className="w-4 h-4 mr-2"/>{f.branch_lat!==null ? `Pinned · ${f.branch_lat},${f.branch_lng}` : "Pin Branch on Map"}
          </Button>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <Input data-testid="lat-input" value={f.branch_lat ?? ""} onChange={e=>setF(p=>({...p, branch_lat: e.target.value===""?null:parseFloat(e.target.value)}))} placeholder="Latitude" className="bg-slate-950 border-slate-700 h-10 font-mono-alt text-xs"/>
            <Input data-testid="lng-input" value={f.branch_lng ?? ""} onChange={e=>setF(p=>({...p, branch_lng: e.target.value===""?null:parseFloat(e.target.value)}))} placeholder="Longitude" className="bg-slate-950 border-slate-700 h-10 font-mono-alt text-xs"/>
          </div>
          {(f.branch_lat!==null && f.branch_lng!==null) && (
            <iframe title="branch preview" className="w-full h-40 rounded-lg mt-2 border border-emerald-500/40" src={`https://maps.google.com/maps?q=${f.branch_lat},${f.branch_lng}&z=15&output=embed`} loading="lazy"/>
          )}
        </div>

        {/* Save */}
        <Button data-testid="save-changes" onClick={submit} disabled={submitting} className="w-full h-12 bg-[#FFD700] hover:bg-[#f0c800] text-slate-900 font-black rounded-full text-sm mt-2">
          {submitting ? "Submitting…" : "Save Changes (Send for Admin Approval)"}
        </Button>
        <div className="text-[10px] text-slate-500 text-center">Changes go live after Admin approval.</div>
      </DialogContent>
    </Dialog>
  );
}
